import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

interface User {
  id: string;
  email: string;
  role: string;
  account_status: string;
  is_active: boolean;
  is_internal_account?: boolean;
  created_at: string;
  last_login: string | null;
  total_compute_hours: number;
  total_spend: number;
  account_balance: number;
  subscription_status: string;
  stripe_customer_id: string | null;
}

interface UsageSummary {
  total_cost: number;
  compute_hours: number;
  sessions: number;
  avg_session_cost: number;
  most_expensive_day: {
    date: string;
    cost: number;
  } | null;
}

interface DailyUsage {
  date: string;
  cost: number;
  hours: number;
  sessions: number;
}

interface ResourceBreakdown {
  resource_type: string;
  provider: string;
  total_cost: number;
  total_hours: number;
  avg_rate: number;
}

interface StripeData {
  customer: any;
  payment_methods: any[];
  recent_invoices: any[];
}

interface AdminAction {
  id: number;
  action_type: string;
  admin_email: string;
  target_email: string | null;
  details: string;
  created_at: string;
}

interface UserDetails {
  user: User;
  usage_summary?: UsageSummary;
  daily_trend?: DailyUsage[];
  resource_breakdown?: ResourceBreakdown[];
  stripe_data?: StripeData;
}

interface Transaction {
  id: number;
  transaction_type: string;
  amount: number;
  balance_before: number;
  balance_after: number;
  description: string | null;
  created_at: string | null;
}

export default function AdminUserDetailsPage() {
  const { userId } = useParams<{ userId: string }>();
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [adminActions, setAdminActions] = useState<AdminAction[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'usage' | 'billing' | 'actions'>('overview');

  // Balance management form state
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustType, setAdjustType] = useState<'adjustment' | 'refund'>('adjustment');
  const [submitting, setSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    if (userId) {
      fetchUserDetails();
      fetchAdminActions();
      fetchTransactions();
    }
  }, [userId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const response = await axios.get(`${getApiUrl()}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUserDetails(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching user details:', err);
      if (err.response?.status === 403) {
        setError('Access denied. Admin privileges required.');
      } else if (err.response?.status === 401) {
        navigate('/login');
      } else if (err.response?.status === 404) {
        setError('User not found.');
      } else {
        setError('Failed to load user details');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminActions = async () => {
    try {
      const token = localStorage.getItem('authToken');

      const response = await axios.get(`${getApiUrl()}/admin/actions?target_user_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAdminActions(response.data.actions || []);
    } catch (err: any) {
      console.error('Error fetching admin actions:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${getApiUrl()}/admin/users/${userId}/transactions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTransactions(response.data.transactions || []);
    } catch (err: any) {
      console.error('Error fetching transactions:', err);
    }
  };

  const handleInternalCredit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0 || !creditReason.trim()) return;

    setSubmitting(true);
    setFormMessage(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${getApiUrl()}/admin/users/${userId}/internal-credit`,
        { amount, reason: creditReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormMessage({
        type: 'success',
        text: `Credited $${amount.toFixed(2)}. New balance: $${response.data.balance_after?.toFixed(2)}`
      });
      setCreditAmount('');
      setCreditReason('');
      fetchUserDetails();
      fetchTransactions();
    } catch (err: any) {
      setFormMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to issue credit' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBalanceAdjust = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = parseFloat(adjustAmount);
    if (!amount || !adjustReason.trim()) return;

    setSubmitting(true);
    setFormMessage(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${getApiUrl()}/admin/users/${userId}/balance`,
        { amount, reason: adjustReason.trim(), transaction_type: adjustType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFormMessage({
        type: 'success',
        text: `${adjustType === 'refund' ? 'Refund' : 'Adjustment'} of $${Math.abs(amount).toFixed(2)} applied. New balance: $${response.data.balance_after?.toFixed(2)}`
      });
      setAdjustAmount('');
      setAdjustReason('');
      fetchUserDetails();
      fetchTransactions();
    } catch (err: any) {
      setFormMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to apply adjustment' });
    } finally {
      setSubmitting(false);
    }
  };

  const getTransactionTypeBadge = (type: string) => {
    switch (type) {
      case 'topup':
        return 'bg-green-500/20 text-green-400';
      case 'internal_credit':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'deduction':
        return 'bg-red-500/20 text-red-400';
      case 'refund':
        return 'bg-blue-500/20 text-blue-400';
      case 'adjustment':
        return 'bg-purple-500/20 text-purple-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-500/20 text-purple-400';
      case 'enterprise':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400';
      case 'suspended':
        return 'bg-red-500/20 text-red-400';
      case 'canceled':
        return 'bg-orange-500/20 text-orange-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
            <div className="text-red-400">{error}</div>
            <button
              onClick={() => navigate('/admin/users')}
              className="mt-4 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              ← Back to Users
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!userDetails) {
    return null;
  }

  const { user } = userDetails;
  const usage_summary = userDetails.usage_summary ?? { total_cost: 0, compute_hours: 0, sessions: 0, avg_session_cost: 0, most_expensive_day: null };
  const resource_breakdown = userDetails.resource_breakdown ?? [];
  const stripe_data = userDetails.stripe_data ?? { customer: null, payment_methods: [], recent_invoices: [] };

  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <h1 className="text-3xl font-bold text-white">{user.email}</h1>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getRoleColor(user.role)}`}>
                {user.role}
              </span>
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(user.account_status)}`}>
                {user.account_status}
              </span>
              {user.is_internal_account && (
                <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-yellow-500/20 text-yellow-400">
                  internal
                </span>
              )}
            </div>
            <p className="text-gray-400">
              User ID: {user.id} • Joined {new Date(user.created_at).toLocaleDateString()}
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => navigate('/admin/users')}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
            >
              ← Back to Users
            </button>
            <button
              onClick={fetchUserDetails}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-8 bg-gray-800 rounded-lg p-1">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'usage', label: 'Usage Analytics' },
            { key: 'billing', label: 'Billing & Stripe' },
            { key: 'actions', label: 'Admin Actions' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as typeof activeTab)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.key
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <div className="text-2xl font-bold text-white">${(user.total_spend || 0).toFixed(2)}</div>
                <div className="text-sm text-gray-400">Total Spend</div>
              </div>
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <div className="text-2xl font-bold text-white">{(user.total_compute_hours || 0).toFixed(1)}h</div>
                <div className="text-sm text-gray-400">Compute Hours</div>
              </div>
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <div className="text-2xl font-bold text-white">${(usage_summary.total_cost || 0).toFixed(2)}</div>
                <div className="text-sm text-gray-400">This Month</div>
              </div>
              <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
                <div className={`text-2xl font-bold ${user.account_balance >= 5 ? 'text-green-400' : user.account_balance >= 2 ? 'text-yellow-400' : 'text-red-400'}`}>
                  ${(user.account_balance || 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-400">Account Balance</div>
                <div className="text-xs text-gray-500 mt-1">
                  {user.account_balance >= 5 ? '✓ Can launch instances' : user.account_balance >= 2 ? '⚠ Low balance' : '⚠ Top-up needed'}
                </div>
              </div>
            </div>

            {/* User Info */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">User Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <div>
                    <span className="text-gray-400">Email:</span>
                    <span className="text-white ml-2">{user.email}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Role:</span>
                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded ${getRoleColor(user.role)}`}>
                      {user.role}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Account Status:</span>
                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded ${getStatusColor(user.account_status)}`}>
                      {user.account_status}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Active:</span>
                    <span className={`ml-2 ${user.is_active ? 'text-green-400' : 'text-red-400'}`}>
                      {user.is_active ? 'Yes' : 'No'}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <span className="text-gray-400">Created:</span>
                    <span className="text-white ml-2">{new Date(user.created_at).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Last Login:</span>
                    <span className="text-white ml-2">
                      {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-400">Subscription:</span>
                    <span className="text-white ml-2">{user.subscription_status}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Stripe Customer:</span>
                    <span className={`ml-2 ${user.stripe_customer_id ? 'text-green-400' : 'text-red-400'}`}>
                      {user.stripe_customer_id ? 'Connected' : 'Not connected'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Usage Tab */}
        {activeTab === 'usage' && (
          <div className="space-y-6">
            {/* Usage Summary */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Usage Summary (Current Month)</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <div className="text-2xl font-bold text-white">${(usage_summary.total_cost || 0).toFixed(2)}</div>
                  <div className="text-sm text-gray-400">Total Cost</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">{(usage_summary.compute_hours || 0).toFixed(1)}h</div>
                  <div className="text-sm text-gray-400">Compute Hours</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">${(usage_summary.avg_session_cost || 0).toFixed(2)}</div>
                  <div className="text-sm text-gray-400">Avg Session Cost</div>
                </div>
              </div>
              {usage_summary.most_expensive_day && (
                <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                  <div className="text-yellow-400 font-medium">Most Expensive Day</div>
                  <div className="text-white">
                    {usage_summary.most_expensive_day?.date ? new Date(usage_summary.most_expensive_day.date).toLocaleDateString() : 'N/A'} - 
                    ${(usage_summary.most_expensive_day?.cost || 0).toFixed(2)}
                  </div>
                </div>
              )}
            </div>

            {/* Resource Breakdown */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Resource Usage Breakdown</h3>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-800/50">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-sm font-medium text-gray-300">Resource Type</th>
                      <th className="px-4 py-3 text-sm font-medium text-gray-300">Provider</th>
                      <th className="px-4 py-3 text-sm font-medium text-gray-300">Total Cost</th>
                      <th className="px-4 py-3 text-sm font-medium text-gray-300">Hours</th>
                      <th className="px-4 py-3 text-sm font-medium text-gray-300">Avg Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {resource_breakdown.map((resource, index) => (
                      <tr key={index} className="hover:bg-gray-800/30">
                        <td className="px-4 py-3 text-sm text-white">{resource.resource_type}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{resource.provider.toUpperCase()}</td>
                        <td className="px-4 py-3 text-sm text-white">${(resource.total_cost || 0).toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-gray-300">{(resource.total_hours || 0).toFixed(1)}h</td>
                        <td className="px-4 py-3 text-sm text-gray-300">${(resource.avg_rate || 0).toFixed(3)}/hr</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* Balance Management */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Balance Management</h3>

              <div className="mb-6">
                <div className={`text-3xl font-bold ${user.account_balance >= 5 ? 'text-green-400' : user.account_balance >= 2 ? 'text-yellow-400' : 'text-red-400'}`}>
                  ${(user.account_balance || 0).toFixed(2)}
                </div>
                <div className="text-sm text-gray-400 mt-1">
                  Current balance
                  {user.is_internal_account && <span className="ml-2 px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-400">internal account</span>}
                </div>
              </div>

              {formMessage && (
                <div className={`mb-4 p-3 rounded-lg text-sm ${
                  formMessage.type === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`}>
                  {formMessage.text}
                </div>
              )}

              <div className={`grid grid-cols-1 ${user.is_internal_account ? 'md:grid-cols-2' : ''} gap-6`}>
                {/* Internal Credit — only for internal accounts */}
                {user.is_internal_account && (
                  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                    <h4 className="text-sm font-semibold text-yellow-400 mb-3">Issue Internal Credit</h4>
                    <form onSubmit={handleInternalCredit} className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Amount ($)</label>
                        <input
                          type="number"
                          min="0.01"
                          step="0.01"
                          value={creditAmount}
                          onChange={(e) => setCreditAmount(e.target.value)}
                          placeholder="100.00"
                          className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Reason</label>
                        <textarea
                          value={creditReason}
                          onChange={(e) => setCreditReason(e.target.value)}
                          placeholder="e.g. Monthly internal testing budget"
                          rows={2}
                          className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500/50 resize-none"
                          required
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 disabled:bg-yellow-800 text-white rounded text-sm font-medium transition-colors"
                      >
                        {submitting ? 'Processing...' : 'Issue Credit'}
                      </button>
                    </form>
                  </div>
                )}

                {/* Balance Adjustment — all users */}
                <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                  <h4 className="text-sm font-semibold text-purple-400 mb-3">Balance Adjustment</h4>
                  <form onSubmit={handleBalanceAdjust} className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Amount ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={adjustAmount}
                          onChange={(e) => setAdjustAmount(e.target.value)}
                          placeholder="+50 or -10"
                          className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1">Type</label>
                        <select
                          value={adjustType}
                          onChange={(e) => setAdjustType(e.target.value as 'adjustment' | 'refund')}
                          className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                        >
                          <option value="adjustment">Adjustment</option>
                          <option value="refund">Refund</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Reason</label>
                      <textarea
                        value={adjustReason}
                        onChange={(e) => setAdjustReason(e.target.value)}
                        placeholder="e.g. Billing error correction"
                        rows={2}
                        className="w-full bg-gray-900 border border-gray-700 text-white rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50 resize-none"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 text-white rounded text-sm font-medium transition-colors"
                    >
                      {submitting ? 'Processing...' : 'Apply Adjustment'}
                    </button>
                  </form>
                </div>
              </div>
            </div>

            {/* Transaction History */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white">Recent Transactions</h3>
                <button
                  onClick={fetchTransactions}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Refresh
                </button>
              </div>
              {transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-800/50">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-sm font-medium text-gray-300">Date</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-300">Type</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-300 text-right">Amount</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-300 text-right">Balance After</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-300">Description</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {transactions.map((txn) => (
                        <tr key={txn.id} className="hover:bg-gray-800/30">
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {txn.created_at ? new Date(txn.created_at).toLocaleString() : '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded ${getTransactionTypeBadge(txn.transaction_type)}`}>
                              {txn.transaction_type.replace('_', ' ')}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-sm text-right font-mono ${txn.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {txn.amount >= 0 ? '+' : ''}${txn.amount.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-right text-gray-300 font-mono">
                            ${txn.balance_after.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-400 max-w-xs truncate">
                            {txn.description || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-6 text-gray-400">No transactions recorded.</div>
              )}
            </div>

            {/* Stripe Customer Info */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Stripe Customer Information</h3>
              {stripe_data.customer ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <span className="text-gray-400">Customer ID:</span>
                      <span className="text-white ml-2 font-mono text-sm">{stripe_data.customer.id}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Name:</span>
                      <span className="text-white ml-2">{stripe_data.customer.name || 'Not set'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Created:</span>
                      <span className="text-white ml-2">
                        {new Date(stripe_data.customer.created * 1000).toLocaleDateString()}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-400">Default Payment:</span>
                      <span className="text-white ml-2">
                        {stripe_data.customer.invoice_settings?.default_payment_method ? 'Set' : 'None'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-gray-400">No Stripe customer associated with this user.</div>
              )}
            </div>

            {/* Payment Methods */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Payment Methods</h3>
              {stripe_data.payment_methods && stripe_data.payment_methods.length > 0 ? (
                <div className="space-y-3">
                  {stripe_data.payment_methods.map((pm: any) => (
                    <div key={pm.id} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center mr-3">
                          <span className="text-white text-sm font-medium">
                            {pm.card?.brand?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="text-white text-sm">
                            **** **** **** {pm.card?.last4}
                          </div>
                          <div className="text-gray-400 text-xs">
                            {pm.card?.brand} • Expires {pm.card?.exp_month}/{pm.card?.exp_year}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-400">No payment methods on file.</div>
              )}
            </div>

            {/* Recent Invoices */}
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Invoices</h3>
              {stripe_data.recent_invoices && stripe_data.recent_invoices.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-800/50">
                      <tr className="text-left">
                        <th className="px-4 py-3 text-sm font-medium text-gray-300">Invoice ID</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-300">Amount</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-300">Status</th>
                        <th className="px-4 py-3 text-sm font-medium text-gray-300">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-800">
                      {stripe_data.recent_invoices.map((invoice: any) => (
                        <tr key={invoice.id} className="hover:bg-gray-800/30">
                          <td className="px-4 py-3 text-sm text-white font-mono">{invoice.number}</td>
                          <td className="px-4 py-3 text-sm text-white">
                            ${(invoice.amount_paid / 100).toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              invoice.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                              invoice.status === 'open' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {invoice.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-300">
                            {new Date(invoice.created * 1000).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-gray-400">No invoices found.</div>
              )}
            </div>
          </div>
        )}

        {/* Admin Actions Tab */}
        {activeTab === 'actions' && (
          <div className="space-y-6">
            <div className="bg-gray-900 rounded-xl border border-gray-800 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Admin Actions History</h3>
              {adminActions.length > 0 ? (
                <div className="space-y-4">
                  {adminActions.map((action) => (
                    <div key={action.id} className="p-4 bg-gray-800/50 rounded-lg border border-gray-700/50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-1 text-xs font-semibold rounded bg-blue-500/20 text-blue-400">
                              {action.action_type}
                            </span>
                            <span className="text-gray-400 text-sm">by {action.admin_email}</span>
                          </div>
                          <div className="text-white text-sm mb-2">{action.details}</div>
                          <div className="text-gray-400 text-xs">
                            {new Date(action.created_at).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-400">No admin actions recorded for this user.</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}